import React from 'react';
import PropTypes from 'prop-types';

import { IComponent } from '../../../typings/generic-component';
import { Color, FontSize, FontWeight } from '../../../typings/design-tokens';
import Text from '../../atoms/text/text';
import { IClassNames, makeClassNames } from '../../../utils/styles';

import './alphabetical-nav.scss';

export interface IAlphabeticalNav extends IComponent {
  wordList: string[];
  currentLetter: string;
  onLetterClick: (letter: string) => void;
}

export const ALPHABETH = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const FONT_SIZE: FontSize = '14';
const FONT_WEIGHT: FontWeight = '500';
const LETTER_COLOR: Color = 'medium-light';

const getInitials = (wordList: string[]) => {
  const initialsObj = wordList.reduce((obj, word) => {
    return { ...obj, [word[0].toUpperCase()]: true };
  }, {});

  return Object.keys(initialsObj);
};
const isLetterDisabled = (letter: string, activeLetters: string[]): boolean => !activeLetters.includes(letter);
const isCurrentLetter = (letter: string, currentLetter: string): boolean => letter === currentLetter;

const classNames = (): IClassNames =>
  makeClassNames({
    alphabeticalNavContainer: 'alphabetical-nav--container',
    alphabeticalNav: 'alphabetical-nav',
    wrapper: 'alphabetical-nav__wrapper',
    letter: 'alphabetical-nav__letter',
    disabled: ['alphabetical-nav__letter', 'alphabetical-nav__letter--disabled'],
    current: ['alphabetical-nav__letter', 'alphabetical-nav__letter--current'],
  });

const AlphabeticalNav: React.FC<IAlphabeticalNav> = ({ wordList, currentLetter, onLetterClick }) => {
  const cn = classNames();

  const activeLettersUpper = getInitials(wordList);
  const currentLetterUpper = currentLetter.toUpperCase();

  const getLetterStyle = (letter: string): string => {
    // eslint-disable-next-line no-nested-ternary
    return isLetterDisabled(letter, activeLettersUpper)
      ? cn.disabled
      : isCurrentLetter(letter, currentLetterUpper)
      ? cn.current
      : cn.letter;
  };

  const onLetterClickHandler = (letter: string) => {
    !isLetterDisabled(letter, activeLettersUpper) ? onLetterClick(letter) : null;
  };

  return (
    <div className={cn.alphabeticalNavContainer}>
      <div className={cn.alphabeticalNav}>
        {ALPHABETH.split('').map((letter) => (
          <div className={cn.wrapper} key={letter} onClick={() => onLetterClickHandler(letter)}>
            <Text
              tag="p"
              className={getLetterStyle(letter)}
              fontSize={FONT_SIZE}
              color={LETTER_COLOR}
              fontWeight={FONT_WEIGHT}
            >
              {letter}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
};

AlphabeticalNav.propTypes = {
  currentLetter: PropTypes.string.isRequired,
  onLetterClick: PropTypes.func.isRequired,
  wordList: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
};

export default AlphabeticalNav;
